import { useState } from "react";
import { Download, Plus, Search, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function PageToolbar({ onNew, onExport, exportName, searchValue, onSearchChange, extra, newLabel = "New", canNew = true }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const exportAll = async () => {
    try {
      const res = await api.get(`/export/${exportName}?all=true`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${exportName}-ALL.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded ALL");
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3 mb-4">
      {canNew && onNew && (
        <Button
          data-testid="toolbar-new-btn"
          onClick={onNew}
          className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white h-9 rounded-md"
        >
          <Plus className="h-4 w-4 mr-1.5" /> {newLabel}
        </Button>
      )}
      <div className="flex-1" />
      {onSearchChange && (
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="toolbar-search"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search…"
            className="h-9 pl-8 w-64"
          />
        </div>
      )}
      {extra}
      {exportName && (
        <Button
          data-testid="toolbar-export-btn"
          onClick={() => onExport ? onExport() : downloadExport(exportName)}
          className="bg-[color:var(--brand-ochre)] hover:bg-[color:var(--brand-ochre-hover)] text-white h-9 rounded-md"
        >
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      )}
      {exportName && isAdmin && (
        <Button
          data-testid="toolbar-export-all-btn"
          onClick={exportAll}
          className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 rounded-md"
          title="Admin: download every historical record in one click (ignores search & filters)"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> Export ALL
        </Button>
      )}
    </div>
  );
}

export async function downloadExport(resource) {
  const res = await api.get(`/export/${resource}`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${resource}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function EmptyState({ label = "No records yet" }) {
  return (
    <div className="py-16 text-center text-slate-400 text-sm" data-testid="empty-state">
      {label}
    </div>
  );
}

export function StatusPill({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
        active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export function useDialog() {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  return { open, setOpen, item, setItem };
}
